import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Megaphone, Search, ChevronLeft, Loader2, Plus, 
  Edit, Trash2, ExternalLink, Eye, MousePointer
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAds() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [ads, setAds] = useState([]);
  const [placements, setPlacements] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  
  // Form state
  const [title, setTitle] = useState('');
  const [placement, setPlacement] = useState('home_hero');
  const [imageUrl, setImageUrl] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/');
      return;
    }
    fetchAds();
  }, [user]);

  const fetchAds = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/ads`, {
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setAds(data.ads);
        setPlacements(data.placements);
      }
    } catch (error) {
      console.error('Error fetching ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setPlacement('home_hero');
    setImageUrl('');
    setLinkUrl('');
    setIsActive(true);
    setStartDate('');
    setEndDate('');
    setEditingAd(null);
  };

  const openEditDialog = (ad) => {
    setEditingAd(ad);
    setTitle(ad.title);
    setPlacement(ad.placement);
    setImageUrl(ad.image_url);
    setLinkUrl(ad.link_url);
    setIsActive(ad.is_active);
    setStartDate(ad.start_date || '');
    setEndDate(ad.end_date || '');
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const adData = {
        title,
        placement,
        image_url: imageUrl,
        link_url: linkUrl,
        is_active: isActive,
        start_date: startDate || null,
        end_date: endDate || null,
      };

      let response;
      if (editingAd) {
        response = await fetch(`${API_URL}/api/admin/ads/${editingAd.ad_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
          body: JSON.stringify(adData),
        });
      } else {
        response = await fetch(`${API_URL}/api/admin/ads`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
          },
          body: JSON.stringify(adData),
        });
      }

      if (response.ok) {
        toast.success(editingAd ? 'Publicité mise à jour' : 'Publicité créée');
        resetForm();
        setDialogOpen(false);
        fetchAds();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  const deleteAd = async (adId) => {
    if (!confirm('Supprimer cette publicité ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin/ads/${adId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token || localStorage.getItem('token')}`,
        },
      });
      if (response.ok) {
        toast.success('Publicité supprimée');
        fetchAds();
      }
    } catch (error) {
      toast.error('Erreur de connexion');
    }
  };

  return (
    <div className="min-h-screen bg-background pt-24 pb-16" data-testid="admin-ads">
      <div className="container-app">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin')}>
              <ChevronLeft className="w-4 h-4 mr-1" />
              Retour
            </Button>
            <div>
              <h1 className="font-heading text-2xl font-semibold">Gestion des publicités</h1>
              <p className="text-muted-foreground">{ads.length} publicités</p>
            </div>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nouvelle publicité
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>
                  {editingAd ? 'Modifier la publicité' : 'Nouvelle publicité'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <Label>Titre</Label>
                  <Input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Nom de la campagne"
                  />
                </div>
                <div>
                  <Label>Emplacement</Label>
                  <Select value={placement} onValueChange={setPlacement}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(placements).map(([key, info]) => (
                        <SelectItem key={key} value={key}>
                          {info.name} ({info.size})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>URL de l'image</Label>
                  <Input
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="https://..."
                  />
                  {imageUrl && (
                    <img src={imageUrl} alt="Preview" className="mt-2 rounded-lg max-h-32 object-cover" />
                  )}
                </div>
                <div>
                  <Label>URL de destination</Label>
                  <Input
                    value={linkUrl}
                    onChange={(e) => setLinkUrl(e.target.value)}
                    placeholder="https://..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Date de début</Label>
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Date de fin</Label>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <Label>Active</Label>
                  <Switch checked={isActive} onCheckedChange={setIsActive} />
                </div>
                <Button className="btn-primary w-full" onClick={handleSubmit}>
                  {editingAd ? 'Mettre à jour' : 'Créer'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Placements Overview */}
        <Card className="dashboard-card mb-6">
          <CardHeader>
            <CardTitle className="font-heading">Emplacements disponibles</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(placements).map(([key, info]) => (
                <div key={key} className="p-3 rounded-lg bg-muted/50 text-center">
                  <p className="font-medium text-sm">{info.name}</p>
                  <p className="text-xs text-muted-foreground">{info.size}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Ads List */}
        <Card className="dashboard-card">
          <CardContent className="pt-6">
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-accent" />
              </div>
            ) : ads.length === 0 ? (
              <div className="text-center py-8">
                <Megaphone className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucune publicité</p>
              </div>
            ) : (
              <div className="space-y-4">
                {ads.map((ad) => (
                  <div
                    key={ad.ad_id}
                    className="flex items-center gap-4 p-4 rounded-xl bg-muted/50"
                  >
                    {ad.image_url && (
                      <img 
                        src={ad.image_url} 
                        alt={ad.title}
                        className="w-24 h-16 rounded-lg object-cover"
                      />
                    )}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium">{ad.title}</p>
                        {ad.is_active ? (
                          <Badge className="bg-success">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {placements[ad.placement]?.name || ad.placement}
                      </p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" />
                          {ad.impressions || 0} vues
                        </span>
                        <span className="flex items-center gap-1">
                          <MousePointer className="w-3 h-3" />
                          {ad.clicks || 0} clics
                        </span>
                        {ad.impressions > 0 && (
                          <span>
                            CTR: {((ad.clicks / ad.impressions) * 100).toFixed(1)}%
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {ad.link_url && (
                        <Button variant="ghost" size="sm" asChild>
                          <a href={ad.link_url} target="_blank" rel="noopener noreferrer">
                            <ExternalLink className="w-4 h-4" />
                          </a>
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(ad)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-destructive"
                        onClick={() => deleteAd(ad.ad_id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
